import axios from 'axios';
import { detectAndTrackOffline } from './offlineFaceDetection';

// Resolve API base URL:
// - If REACT_APP_API_BASE is provided (e.g. "http://192.168.1.16:5000"), use it.
// - Otherwise, when running in the browser, use the current page hostname with port 5000.
//   This makes the client work when accessed from another device on the LAN.
const resolvedBase = (function () {
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const proto = window.location.protocol || 'http:';
    const hostname = window.location.hostname;
    
    // In production (no localhost), use same domain without port
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `${proto}//${hostname}`;
    }
    
    // In development (localhost), use port 5000
    return `${proto}//${hostname}:5000`;
  }
  // Fallback for non-browser environments
  return 'http://localhost:5000';
})();

const api = axios.create({
  baseURL: `${resolvedBase}/api`,
  headers: { Accept: 'application/json' },
  timeout: 20000,
});

// Face service API configuration
// In development: use port 5001 (separate service)
// In production: use same domain (proxied through main API)
const faceServiceBase = (function () {
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const hostname = window.location.hostname;
    
    // In production (no localhost), use same domain (backend should proxy to face-service)
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return resolvedBase;
    }
    
    // In development (localhost), use port 5001
    const proto = window.location.protocol || 'http:';
    return `${proto}//${hostname}:5001`;
  }
  return 'http://localhost:5001';
})();

const faceServiceApi = axios.create({
  baseURL: `${faceServiceBase}/api`,
  headers: { Accept: 'application/json' },
  timeout: 20000,
});

// Add response interceptor for offline handling
faceServiceApi.interceptors.response.use(
  response => response,
  error => {
    // Check if error is due to offline/network issues
    if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !navigator.onLine)) {
      console.log('[API Interceptor] Network/offline error detected:', error.code);
      // Return graceful offline response
      return Promise.resolve({
        data: {
          offline: true,
          success: false,
          message: 'Offline - Using local mode',
          tracks: [],
          count: 0
        }
      });
    }
    return Promise.reject(error);
  }
);

// Add response interceptor for main API as well
api.interceptors.response.use(
  response => response,
  error => {
    // Check if error is due to offline/network issues
    if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !navigator.onLine)) {
      console.log('[API Interceptor] Network/offline error for main API:', error.code);
      // Return graceful offline response
      return Promise.resolve({
        data: {
          offline: true,
          success: false,
          message: 'Offline - Using local mode',
          students: [],
          error: 'Offline'
        }
      });
    }
    return Promise.reject(error);
  }
);

export async function trackFaces(detections, image = null) {
  // Filter out invalid detections with negative or out-of-bounds coordinates
  const validDetections = detections.filter(det => {
    const [x1, y1] = det.topLeft;
    const [x2, y2] = det.bottomRight;
    const isValid = x1 >= 0 && y1 >= 0 && x2 > x1 && y2 > y1 && x1 < 10000 && y1 < 10000;
    if (!isValid) {}
    return isValid;
  });

  // Convert to backend format: {x1, y1, x2, y2, score}
  const formattedDetections = validDetections.map(det => ({
    x1: det.topLeft[0],  // x coordinate
    y1: det.topLeft[1],  // y coordinate
    x2: det.bottomRight[0],
    y2: det.bottomRight[1],
    score: det.probability
  }));

  if (formattedDetections.length === 0) {
    return { tracks: [], count: 0 };
  }

  try {
    console.log('[API] trackFaces: Sending request to /api/track_faces');
    
    // Use fetch instead of axios so Service Worker can intercept
    const response = await fetch(`${faceServiceBase}/api/track_faces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        detections: formattedDetections,
        image
      })
    });

    const data = await response.json();
    
    // If offline response received
    if (data.offline) {
      console.log('[API] Offline - track_faces not available, using local cache');
      return { tracks: [], count: 0, offline: true };
    }

    return data;
  } catch (err) {
    console.log('[API] trackFaces error (expected if offline):', err.message);
    return { tracks: [], count: 0, offline: true };
  }
}

/**
 * RECOMMENDED: Detect and track faces using backend models (no frontend SCRFD issues)
 * Sends image to backend, receives clean tracked faces with stable IDs
 */
export async function detectAndTrack(imageDataUrl) {
  if (!imageDataUrl) {
    return { tracks: [], count: 0 };
  }

  try {
    console.log('[API] detectAndTrack: Sending request to /api/detect_and_track');
    
    // Use fetch instead of axios so Service Worker can intercept
    const response = await fetch(`${faceServiceBase}/api/detect_and_track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: imageDataUrl })
    });

    const data = await response.json();

    // If offline response received, use client-side detection
    if (data.offline) {
      console.log('[API] API unavailable - falling back to offline face detection');
      const offlineResult = await detectAndTrackOffline(imageDataUrl);
      return {
        ...offlineResult,
        count: offlineResult.tracks?.length || 0
      };
    }

    return data;
  } catch (err) {
    console.log('[API] detectAndTrack error - falling back to offline detection:', err.message);
    
    // Try offline detection as fallback
    try {
      const offlineResult = await detectAndTrackOffline(imageDataUrl);
      return {
        ...offlineResult,
        count: offlineResult.tracks?.length || 0
      };
    } catch (offlineErr) {
      console.log('[API] Offline detection also failed:', offlineErr.message);
      return { tracks: [], count: 0, offline: true, error: offlineErr.message };
    }
  }
}

export async function resetTracker() {
  try {
    console.log('[API] resetTracker: Sending request to /api/reset_tracker');
    
    // Use fetch instead of axios so Service Worker can intercept
    const response = await fetch(`${faceServiceBase}/api/reset_tracker`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await response.json();

    // If offline response received
    if (data.offline) {
      console.log('[API] Offline - reset_tracker not available');
      return { success: false, offline: true };
    }

    return data;
  } catch (err) {
    console.log('[API] resetTracker error (expected if offline):', err.message);
    return { success: false, offline: true };
  }
}

export async function getStudents() {
  const res = await api.get('/students');
  return res.data;
}

export async function registerStudent(student) {
  if (student.image) {
    const form = new FormData();
    form.append('name', student.name);
    if (student.rollNo) form.append('rollNo', student.rollNo);
    if (student.class) form.append('class', student.class);
    if (student.section) form.append('section', student.section);
    if (student.gender) form.append('gender', student.gender);
    if (student.dob) form.append('dob', student.dob);
    if (student.contact) form.append('contact', student.contact);
    const file = student.image instanceof File ? student.image : new File([student.image], 'photo.jpg', { type: 'image/jpeg' });
    form.append('image', file);
    const res = await api.post('/students/register', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  }
  const res = await api.post('/students/register', student);
  return res.data;
}

export async function uploadFaceImage(image) {
  // If image is a base64/data URL string, send JSON { image }
  if (typeof image === 'string') {
    const res = await api.post('/students/face', { image }, { headers: { 'Content-Type': 'application/json' } });
    return res.data;
  }
  // Otherwise send as multipart/form-data
  const form = new FormData();
  const file = image instanceof File ? image : new File([image], 'face.jpg', { type: 'image/jpeg' });
  form.append('image', file);
  const res = await api.post('/students/face', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data;
}

export async function captureAttendance(image) {
  // If a data URL / base64 string is provided, send JSON { image }
  if (typeof image === 'string') {
    const res = await api.post('/attendance/capture', { image }, { headers: { 'Content-Type': 'application/json' } });
    return res.data;
  }
  // Otherwise send as multipart/form-data
  const form = new FormData();
  const file = image instanceof File ? image : new File([image], 'classroom.jpg', { type: 'image/jpeg' });
  form.append('image', file);
  const res = await api.post('/attendance/capture', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data;
}

export async function detectFaces(image) {
  // Call backend server which proxies to face-service
  try {
    const res = await api.post('/attendance/detect-faces', { image }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 5000
    });
    return res.data;
  } catch (err) {
    return { faces: [], count: 0, error: err.message };
  }
}

// Face recognition and auto-attendance marking
export async function recognizeAndMarkAttendance(imageBase64) {
  try {
    const res = await api.post('/attendance/recognize-face', { image: imageBase64 }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 10000
    });
    return res.data;
  } catch (err) {
    return { error: err.message, recognized: false };
  }
}

/**
 * FAST: Batch recognize multiple face embeddings at once
 * Input: Array of { trackId, embedding } objects
 * Output: Array of recognition results with matched students
 */
export async function batchRecognizeEmbeddings(embeddingsData) {
  try {
    console.log('[API] batchRecognizeEmbeddings: Sending request');
    
    // Use fetch instead of axios so Service Worker can intercept
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${faceServiceBase}/api/match_embeddings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ embeddings: embeddingsData }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    const data = await response.json();
    
    if (data.offline) {
      console.log('[API] Offline - match_embeddings not available');
      return { matches: [], offline: true };
    }
    
    return data;
  } catch (err) {
    console.log('[API] batchRecognizeEmbeddings error:', err.message);
    return { matches: [], error: err.message, offline: true };
  }
}

// ============ VENUE LOCATION API FUNCTIONS ============

/**
 * Upload venue images to Cloudinary via backend
 * @param {File[]} files - Array of File objects
 * @returns {{ urls: string[] }}
 */
export async function uploadVenueImages(files) {
  const form = new FormData();
  files.forEach((file) => form.append('images', file));
  const res = await api.post('/vbsa/venue-locations/upload-images', form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
}

/**
 * Search venues from VBSA platform by venue code or name
 */
export async function searchVenues(search) {
  try {
    const res = await api.get('/vbsa/venue-locations/search-venues', {
      params: { search, page: 1, page_size: 10 }
    });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to search venues');
  }
}

/**
 * Register a new venue location
 */
export async function registerVenueLocation(data) {
  try {
    const res = await api.post('/vbsa/venue-locations', data, {
      headers: { 'Content-Type': 'application/json' }
    });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to register venue location');
  }
}

/**
 * Get all venue locations with pagination and filters
 */
export async function getVenueLocations(params = '') {
  try {
    const res = await api.get(`/vbsa/venue-locations${params}`);
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to fetch venue locations');
  }
}

/**
 * Get a specific venue location by ID
 */
export async function getVenueLocationById(id) {
  try {
    const res = await api.get(`/vbsa/venue-locations/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to fetch venue location');
  }
}

/**
 * Update a venue location
 */
export async function updateVenueLocation(id, data) {
  try {
    const res = await api.put(`/vbsa/venue-locations/${id}`, data, {
      headers: { 'Content-Type': 'application/json' }
    });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to update venue location');
  }
}

/**
 * Delete a venue location
 */
export async function deleteVenueLocation(id) {
  try {
    const res = await api.delete(`/vbsa/venue-locations/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to delete venue location');
  }
}

/**
 * Get all venue locations for a specific user by mobile number
 */
export async function getUserVenueLocations(mobile) {
  try {
    const res = await api.get(`/vbsa/venue-locations/user/${mobile}`);
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to fetch user venue locations');
  }
}

/**
 * Approve a venue location (Admin only)
 */
export async function approveVenueLocation(id, comments = '') {
  try {
    const res = await api.put(`/vbsa/venue-locations/${id}/approve`, {
      approverComments: comments
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to approve venue location');
  }
}

/**
 * Reject a venue location (Admin only)
 */
export async function rejectVenueLocation(id, reason) {
  try {
    const res = await api.put(`/vbsa/venue-locations/${id}/reject`, {
      rejectionReason: reason
    }, {
      headers: { 'Content-Type': 'application/json' }
    });
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to reject venue location');
  }
}

/**
 * Get all active venue locations
 */
export async function getActiveVenueLocations() {
  try {
    const res = await api.get('/vbsa/venue-locations/status/active');
    return res.data;
  } catch (err) {
    throw new Error(err.response?.data?.message || err.message || 'Failed to fetch active venue locations');
  }
}

export default api;
